"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CircuitBreakerProxy = void 0;
const circuitBreakerState_1 = require("./circuitBreakerState");
const circuitBreakerError_1 = require("./circuitBreakerError");
const guard_1 = require("../utils/guard");
const utils_1 = require("./utils");
const leakingBucket_1 = require("./leakingBucket");
/**
 * A circuit breaker to protect a caller from failing resources.
 */
class CircuitBreakerProxy {
    /**
     * Initializes a new instance of the @see CircuitBreaker class.
     * @param breakDurationMs Minimum time in milli seconds circuit breaker will stay in open state.
     * @param maxFailedCalls Maximum failed calls before opening the circuit breaker.
     * @param leakTimeSpanInMilliSeconds Timespan in milliseconds in within errors are counted against max failed calls.
     * @param logger Logger to use.
     * @param stateChangedCallback Callback to invoke if internal state has changed.
     * @param initialState The initial state of the circuit breaker.
     */
    constructor(breakDurationMs, maxFailedCalls, leakTimeSpanInMilliSeconds, logger, stateChangedCallback, initialState = circuitBreakerState_1.CircuitBreakerState.Close) {
        guard_1.Guard.throwIfNullOrEmpty(breakDurationMs, 'breakDurationMs');
        guard_1.Guard.throwIfNullOrEmpty(maxFailedCalls, 'maxFailedCalls');
        guard_1.Guard.throwIfNullOrEmpty(leakTimeSpanInMilliSeconds, 'leakTimeSpanInMilliSeconds');
        guard_1.Guard.throwIfNullOrEmpty(logger, 'logger');
        this.breakDurationMs = breakDurationMs;
        this.stateChangedCallback = stateChangedCallback;
        this.bucket = new leakingBucket_1.LeakingBucket(leakTimeSpanInMilliSeconds, maxFailedCalls);
        this.logger = logger;
        this.state = initialState;
        this.openExpiration = new Date();
        // Set to past to have a valid initial state.
        this.openExpiration.setSeconds(this.openExpiration.getSeconds() - 5);
    }
    /**
     * Gets the current state of the circuit breaker.
     * @returns The current state of the circuit breaker.
     */
    getState() {
        return this.state;
    }
    /**
     * Updates the internal state.
     * @param newState The new state.
     * @param requestId Request Guid.
     */
    setState(newState, requestId) {
        this.logger.warning(requestId, `Circuit Breaker state changed from '${this.state}' to '${newState}'`, null, utils_1.logFormatter);
        this.state = newState;
        if (this.stateChangedCallback) {
            this.stateChangedCallback(newState);
        }
    }
    /**
     * Reset error count to zero.
     */
    resetErrorCount() {
        this.logger.warning(null, 'Maintenance: CircuitBreaker reset error count to zero', null, utils_1.logFormatter);
        this.bucket.clear();
    }
    /**
     * Executes a function within a circuit breaker proxy.
     * @param func Function to execute within the circuit breaker proxy.
     * @param requestId Request Guid.
     */
    execute(func, requestId) {
        return __awaiter(this, void 0, void 0, function* () {
            this.logger.trace(requestId, `Circuit Breaker in '${this.state}' state.`, null, utils_1.logFormatter);
            switch (this.state) {
                case circuitBreakerState_1.CircuitBreakerState.Close:
                    const handleClose = yield this.callFunc(func, false, requestId);
                    const successClose = handleClose[0];
                    if (successClose) {
                        return handleClose[1];
                    }
                    else {
                        const error = this.handleError(handleClose[2], false, requestId);
                        this.currentError = error;
                        throw error;
                    }
                case circuitBreakerState_1.CircuitBreakerState.HalfOpen:
                    const handleHalfOpen = yield this.callFunc(func, true, requestId);
                    const successHalfOpen = handleHalfOpen[0];
                    if (successHalfOpen) {
                        return handleHalfOpen[1];
                    }
                    else {
                        const error = this.handleError(handleHalfOpen[2], true, requestId);
                        this.currentError = error;
                        throw error;
                    }
                case circuitBreakerState_1.CircuitBreakerState.Open:
                    const now = new Date().getTime();
                    const exp = this.openExpiration.getTime();
                    if (now > exp) {
                        this.openExpiration = new Date();
                        this.setState(circuitBreakerState_1.CircuitBreakerState.HalfOpen, requestId);
                        return this.execute(func, requestId);
                    }
                    else {
                        const text = `Circuit Breaker is in open state. ${requestId ||
                            'Func'} will be tried again after '${this.openExpiration}'.`;
                        this.logger.debug(requestId, text, null, utils_1.logFormatter);
                        throw new circuitBreakerError_1.CircuitBreakerError(text, this.currentError);
                    }
                default:
                    throw new Error(`State '${this.state}' is unkown!`);
            }
        });
    }
    /**
     * Handles the call to the real function in the circuit breaker.
     * @param func The real func to call.
     * @param wasHalfOpen Flag if circuit breaker is currently in half open state.
     * @param requestId Request Guid.
     */
    callFunc(func, wasHalfOpen, requestId) {
        return __awaiter(this, void 0, void 0, function* () {
            let result;
            let success;
            let error;
            try {
                result = yield func();
                success = true;
                this.logger.debug(requestId, ` in Circuit Breaker called succesfully.`, null, utils_1.logFormatter);
                if (wasHalfOpen) {
                    this.bucket.clear();
                    this.setState(circuitBreakerState_1.CircuitBreakerState.Close, requestId);
                }
            }
            catch (e) {
                success = false;
                error = e;
                this.logger.debug(requestId, ` in Circuit Breaker failed with '${error.message}'.`, null, utils_1.logFormatter);
            }
            const response = [success, result, error];
            return response;
        });
    }
    /**
     * Handles the error if a func call failed.
     * @param error Error that has occured during the func call.
     * @param wasHalfOpen Flag if circuit breaker was currently half open.
     * @param requestId Request Guid.
     * @returns The error to return to the caller.
     */
    handleError(error, wasHalfOpen, requestId) {
        this.bucket.insert(new Date());
        if (this.bucket.isFull() || wasHalfOpen) {
            const expiration = new Date();
            expiration.setMilliseconds(expiration.getMilliseconds() + this.breakDurationMs);
            this.openExpiration = expiration;
            this.logger.debug(requestId, `Circuit Breaker expiration set to '${expiration}'.`, null, utils_1.logFormatter);
            this.setState(circuitBreakerState_1.CircuitBreakerState.Open, requestId);
        }
        return new circuitBreakerError_1.CircuitBreakerError('An error occured during execution of function in Circuit Breaker', error);
    }
}
exports.CircuitBreakerProxy = CircuitBreakerProxy;
//# sourceMappingURL=circuitBreakerProxy.js.map